import { useState } from "react";
import api from "../../api";
import {
  ConnectionMetrics,
  ConnectionQuality,
  ServerHealth,
  classificarConexao,
  montarConclusao,
} from "../../utils/diagnostico";

const PING_SAMPLES = 10;
const PING_TRIM_SLOWEST = 2;
const DOWNLOAD_CHUNK_KB = 4096;
const UPLOAD_CHUNK_BYTES = 2 * 1024 * 1024;
const PARALLEL_STREAMS = 4;
const DOWNLOAD_WINDOW_MS = 3000;
const UPLOAD_WINDOW_MS = 2500;
const DOWNLOAD_MAX_BYTES = 30 * 1024 * 1024;
const UPLOAD_MAX_BYTES = 15 * 1024 * 1024;
const BITS_PER_BYTE = 8;
const BITS_PER_MEGABIT = 1_000_000;

interface ServerStatusResponse {
  status: ServerHealth;
  dbLatencyMs: number;
  serverTimeUtc: string;
}

export interface DiagnosticoResultado {
  metrics: ConnectionMetrics;
  conexao: ConnectionQuality;
  servidor: ServerHealth;
  servidorLatencyMs: number;
  conclusao: string;
}

interface Estagio {
  progress: number;
  label: string;
}

const ESTAGIOS = {
  latencia: { progress: 25, label: "Medindo a estabilidade da conexão..." },
  download: { progress: 55, label: "Medindo a velocidade de download..." },
  upload: { progress: 80, label: "Medindo a velocidade de upload..." },
  servidor: { progress: 95, label: "Verificando o nosso servidor..." },
  concluido: { progress: 100, label: "Concluído" },
};

function mediana(valores: number[]): number {
  if (valores.length === 0) return 0;
  const ordenados = [...valores].sort((a, b) => a - b);
  return ordenados[Math.floor(ordenados.length / 2)];
}

function desvioPadrao(valores: number[]): number {
  if (valores.length === 0) return 0;
  const media = valores.reduce((soma, v) => soma + v, 0) / valores.length;
  const variancia = valores.reduce((soma, v) => soma + (v - media) ** 2, 0) / valores.length;
  return Math.sqrt(variancia);
}

function mbps(bytes: number, segundos: number): number {
  if (segundos <= 0) return 0;
  return (bytes * BITS_PER_BYTE) / (segundos * BITS_PER_MEGABIT);
}

function cacheBuster(): string {
  return `${Date.now()}-${Math.random()}`;
}

async function medirLatencia(): Promise<{ latencyMs: number; jitterMs: number }> {
  const amostras: number[] = [];
  for (let i = 0; i < PING_SAMPLES; i++) {
    const inicio = performance.now();
    await api.get("/Diagnostico/ping", { params: { _t: cacheBuster() } });
    amostras.push(performance.now() - inicio);
  }
  amostras.sort((a, b) => a - b);
  const filtradas = amostras.slice(0, Math.max(1, amostras.length - PING_TRIM_SLOWEST));
  return { latencyMs: mediana(filtradas), jitterMs: desvioPadrao(filtradas) };
}

async function medirVazaoParalela(
  windowMs: number,
  maxBytes: number,
  transferir: (signal: AbortSignal, registrar: (delta: number) => void) => Promise<void>
): Promise<number> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), windowMs);
  let totalBytes = 0;
  const registrar = (delta: number) => {
    totalBytes += delta;
    if (totalBytes >= maxBytes && !controller.signal.aborted) controller.abort();
  };

  const inicio = performance.now();
  const streams = Array.from({ length: PARALLEL_STREAMS }, async () => {
    while (!controller.signal.aborted) {
      try {
        await transferir(controller.signal, registrar);
      } catch {
        break;
      }
    }
  });

  await Promise.all(streams);
  clearTimeout(timer);
  const segundos = (performance.now() - inicio) / 1000;
  return mbps(totalBytes, segundos);
}

async function medirDownload(): Promise<number> {
  return medirVazaoParalela(DOWNLOAD_WINDOW_MS, DOWNLOAD_MAX_BYTES, async (signal, registrar) => {
    let ultimo = 0;
    await api.get("/Diagnostico/download", {
      params: { sizeKb: DOWNLOAD_CHUNK_KB, _t: cacheBuster() },
      responseType: "arraybuffer",
      signal,
      onDownloadProgress: (evento) => {
        registrar(evento.loaded - ultimo);
        ultimo = evento.loaded;
      },
    });
  });
}

async function medirUpload(): Promise<number> {
  const payload = new Uint8Array(UPLOAD_CHUNK_BYTES);
  return medirVazaoParalela(UPLOAD_WINDOW_MS, UPLOAD_MAX_BYTES, async (signal, registrar) => {
    let ultimo = 0;
    await api.post("/Diagnostico/upload", payload, {
      headers: { "Content-Type": "application/octet-stream" },
      signal,
      onUploadProgress: (evento) => {
        registrar(evento.loaded - ultimo);
        ultimo = evento.loaded;
      },
    });
  });
}

export default function useDiagnostico() {
  const [testando, setTestando] = useState(false);
  const [estagio, setEstagio] = useState<Estagio>(ESTAGIOS.latencia);
  const [resultado, setResultado] = useState<DiagnosticoResultado | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  async function executarTeste() {
    setTestando(true);
    setResultado(null);
    setErro(null);

    try {
      setEstagio(ESTAGIOS.latencia);
      const { latencyMs, jitterMs } = await medirLatencia();

      setEstagio(ESTAGIOS.download);
      const downloadMbps = await medirDownload();

      setEstagio(ESTAGIOS.upload);
      const uploadMbps = await medirUpload();

      setEstagio(ESTAGIOS.servidor);
      const response = await api.get("/Diagnostico/status");
      const servidor = response.data as ServerStatusResponse;

      const metrics: ConnectionMetrics = { latencyMs, jitterMs, downloadMbps, uploadMbps };
      const conexao = classificarConexao(metrics);

      setEstagio(ESTAGIOS.concluido);
      setResultado({
        metrics,
        conexao,
        servidor: servidor.status,
        servidorLatencyMs: servidor.dbLatencyMs,
        conclusao: montarConclusao(conexao, servidor.status),
      });
    } catch {
      setErro(
        "Não foi possível concluir o teste. Isso costuma indicar que a sua conexão está muito instável ou sem acesso à internet neste momento."
      );
    } finally {
      setTestando(false);
    }
  }

  return { testando, estagio, resultado, erro, executarTeste };
}
